import { ROLES } from "../constants";

// Feature tours. Each runs automatically the first time a user with one of `roles` reaches `path`
// (null = any page), and can be replayed from the Tours menu in the header.
// Bump `version` when a tour changes meaningfully, and mark new or changed steps with `since: <version>`:
// people who saw an older version get a short "What's new" with just those steps.
// `requires`: the tour auto-starts only once this element is on screen. `prepare`: asked of the page
// (via an `irg:tour-prepare` event) when the tour is started from the menu and `requires` is missing.
// Targets are data-tour attributes on the UI, or functions for upstream markup we don't own.
// Steps whose target isn't on screen (e.g. Install app when the browser can't install) are skipped.

const ALL_ROLES = [ROLES.ADMIN, ROLES.EMPLOYEE, ROLES.MANAGER, ROLES.HR];

const byText = (selector, text) => () =>
  [...document.querySelectorAll(selector)].find((el) => el.textContent.trim().includes(text) && el.offsetParent !== null) || null;

export const TOURS = [
  {
    id: "welcome",
    version: 2,
    title: "Welcome tour",
    description: "A quick look around: navigation, chat, the notice board, your profile and where to find tours.",
    roles: ALL_ROLES,
    path: null,
    steps: [
      {
        target: "body",
        placement: "center",
        title: "Welcome to IRG ERP 👋",
        content: "Here's a quick look at what's new. You can replay any tour later from the Tours button in the header.",
      },
      {
        target: 'nav[aria-label="Main navigation"]',
        placement: "right",
        title: "Navigation",
        content: "Everything you have access to lives here. The menu adapts to your role.",
      },
      {
        target: '[data-tour="header-chat"]',
        title: "Chat",
        content: "Real-time chat with your team: direct messages, groups, files, reactions and @mentions. The badge counts unread messages.",
      },
      {
        target: () => document.querySelector('nav[aria-label="Main navigation"] a[href="/notices"]'),
        placement: "right",
        since: 2,
        title: "Notice Board 📢",
        content: "Announcements from HR and management. The badge shows how many notices you haven't read yet.",
      },
      {
        target: '[data-tour="notice-card"]',
        since: 2,
        title: "Notices on your dashboard",
        content:
          "Pinned and latest notices appear right on your dashboard. A blue dot means unread, and a yellow banner shows when HR needs you to acknowledge something.",
      },
      {
        target: '[data-tour="header-install"]',
        title: "Get the chat app",
        content: "Install IRG Chat, team chat as its own app with a window, a taskbar or Home Screen icon and notifications.",
      },
      {
        target: '[data-tour="header-theme"]',
        title: "Light or dark",
        content: "Switch between light and dark mode.",
      },
      {
        target: '[data-tour="header-profile-menu"]',
        title: "Your profile",
        content: "Open My Profile to add a photo and a short bio, or to change your password.",
      },
      {
        target: '[data-tour="header-tours"]',
        title: "Tours",
        content: "Replay this or any other tour from here. A dot appears when there's a tour you haven't seen.",
      },
    ],
  },
  {
    id: "chat",
    version: 2,
    title: "Chat",
    description: "Start chats and groups, pin chats, search, notifications and chat bubbles.",
    roles: ALL_ROLES,
    path: "/admin/chat",
    steps: [
      { target: '[data-tour="chat-new"]', title: "Start a chat", content: "Message anyone in the company directly." },
      {
        target: '[data-tour="chat-new-group"]',
        title: "Create a group",
        content: "Group admins can add people, set a group photo, manage members and delete the group.",
      },
      {
        target: '[data-tour="chat-search"]',
        title: "Search everything",
        content: "Find people, chats, and words inside messages or file names.",
      },
      {
        target: '[data-tour="chat-list"]',
        placement: "right",
        title: "Your conversations",
        content: "Unread counts, online dots and typing indicators update live. An @ badge means someone mentioned you.",
      },
      {
        target: () => document.querySelector("[data-chat-list-item]"),
        placement: "right",
        since: 2,
        title: "Pin important chats",
        content:
          "Hover a chat and click the pin to keep it at the top of your list (up to 5). Pins are just for you. You can also pin from the chat's details.",
      },
      {
        target: '[data-tour="chat-settings"]',
        since: 2,
        title: "Sound & notifications",
        content:
          "Turn the message sound and desktop notifications on or off, and get notified even when the app is closed (on the installed app and the live site).",
      },
      {
        target: "body",
        placement: "center",
        title: "Mentions & chat bubbles",
        content:
          "In a group, type @ to mention someone, or @everyone to reach the whole group. Mentions get through even if the group is muted. On other pages, open chats float as round bubbles. Drag each one wherever suits you, or onto the × at the bottom of the screen to close it.",
        since: 2,
      },
    ],
  },
  {
    id: "chat-conversation",
    version: 1,
    title: "Inside a chat",
    description: "Message actions, pinned messages, files and the viewer, voice notes and read receipts.",
    roles: ALL_ROLES,
    path: "/admin/chat",
    requires: '[data-tour="chat-composer"]',
    prepare: "open-conversation",
    steps: [
      {
        target: '[data-tour="chat-details"]',
        title: "Chat details",
        content: "Members, group photo and name, plus Pin to top, Mute and Delete chat.",
      },
      {
        target: "[data-chat-pinned-bar]",
        title: "Pinned messages",
        content: "Messages pinned by anyone in the chat show here. Click to jump to one, or open All to see every pin.",
      },
      {
        target: () => [...document.querySelectorAll('section [id^="chat-msg-"]')].pop() || null,
        placement: "top",
        title: "Message actions",
        content:
          "Hover a message to react, reply or pin it for everyone. On your own messages you can also edit, delete and, in groups, see exactly who has read it.",
      },
      {
        target: '[data-tour="chat-attach"]',
        placement: "top",
        title: "Files, images & PDFs",
        content:
          "Attach files up to 25 MB, or paste an image. Click an image or PDF in the chat to open the viewer: zoom, rotate, browse every file in the chat and download.",
      },
      {
        target: '[data-tour="chat-voice"]',
        placement: "top",
        title: "Voice notes",
        content: "With the message box empty, the mic records a voice note (up to 5 minutes). Listen back, then send or discard.",
      },
      {
        target: '[data-tour="chat-composer"]',
        placement: "top",
        title: "Write a message",
        content:
          "Enter sends, Shift+Enter adds a line. Links get a preview card. In groups, type @ to mention someone and look for small avatars under your message to see who has read it.",
      },
    ],
  },
  {
    id: "profile",
    version: 1,
    title: "Your profile",
    description: "Profile photo, bio and password.",
    roles: ALL_ROLES,
    path: "/profile",
    steps: [
      {
        target: '[data-tour="profile-photo"]',
        title: "Profile photo",
        content: "Click the camera to upload and crop a photo. It appears in chat, the header and across the app.",
      },
      {
        target: '[data-tour="profile-edit"]',
        title: "About you",
        content: "Add a short bio that colleagues can see. If HR has linked your account, your name and job title come from your employee record.",
      },
      {
        target: '[data-tour="profile-tabs"]',
        title: "More sections",
        content: "Employment shows your HR details (only you, HR and admins can see them). Security lets you change your password.",
      },
    ],
  },
  {
    id: "notice-board",
    version: 1,
    title: "Notice Board",
    description: "Read, filter and acknowledge notices from HR, with images and files.",
    roles: ALL_ROLES,
    path: "/notices",
    steps: [
      {
        target: '[data-tour="notice-header"]',
        title: "The Notice Board 📢",
        content:
          "Every announcement from HR and management lives here. New notices pop up while you're online, and you get a notification when the app is closed.",
      },
      {
        target: '[data-tour="notice-filters"]',
        title: "Filter notices",
        content: "Show only unread notices, the ones waiting for your acknowledgement, or a category such as Policy or Holiday.",
      },
      {
        target: '[data-tour="notice-search"]',
        title: "Search",
        content: "Find an older notice by any word in its title or text.",
      },
      {
        target: () => document.querySelector("[data-notice-item]"),
        title: "Open a notice",
        content:
          "Click a notice to open it as a post. A blue dot means you haven't read it, a thumbnail means it has images, and \"Acknowledgement needed\" means HR wants you to confirm you've read it.",
      },
      {
        target: "body",
        placement: "center",
        title: "Inside a notice",
        content:
          "Click an image to open it full screen: zoom, rotate and swipe between images. PDFs open in the app too, and other files download. If HR asks for it, press \"I have read and understood\" at the bottom.",
      },
    ],
  },
  {
    id: "notice-posting",
    version: 1,
    title: "Posting notices",
    description: "Post, schedule and pin notices, and see who has read them.",
    roles: [ROLES.ADMIN, ROLES.HR],
    path: "/notices",
    steps: [
      {
        target: '[data-tour="notice-new"]',
        title: "Post a notice",
        content: "Write an announcement for everyone. You can also post from the Notice Board card on any dashboard.",
      },
      {
        target: "body",
        placement: "center",
        title: "Notice options",
        content:
          "Choose a category and priority, pin it to the top, ask everyone to acknowledge it, schedule it for later or have it taken down on a date. Add up to 5 files: images appear as a gallery in the post.",
      },
      {
        target: '[data-tour="notice-status-tabs"]',
        title: "Live, scheduled and expired",
        content: "Employees only see live notices. Check here for notices waiting to go live and ones that have expired.",
      },
      {
        target: () => document.querySelector("[data-notice-item]"),
        title: "Who has read it",
        content:
          "Each notice shows how many people have seen and acknowledged it. Open it and use Read receipts to see exactly who hasn't, or the ⋯ menu to edit, pin or delete.",
      },
    ],
  },
  {
    id: "hr-linking",
    version: 1,
    title: "Linking employees to accounts",
    description: "Connect HR employee records to user logins.",
    roles: [ROLES.ADMIN, ROLES.HR],
    path: "/hr/employees",
    steps: [
      {
        target: byText("button", "Add Employee"),
        title: "Employee records",
        content: "When adding or editing an employee you can now set Designation, Department and a linked user account.",
      },
      {
        target: byText("th", "Linked Account"),
        title: "Linked accounts",
        content:
          "Shows which login each employee uses. Linked people appear in chat and on their profile with their HR name, title and department. Each account can be linked to one employee.",
      },
    ],
  },
  {
    id: "user-management",
    version: 1,
    title: "User accounts & profiles",
    description: "Linked employees and profile pages.",
    roles: [ROLES.ADMIN],
    path: "/admin/user-management",
    steps: [
      {
        target: byText("th", "Linked Employee"),
        title: "Linked employee",
        content: "See which HR employee record each account belongs to. Click it to open the HR record.",
      },
      {
        target: () => document.querySelector('tbody a[href^="/profile/"]'),
        title: "Profiles",
        content: "Click a name to open that person's profile.",
      },
    ],
  },
];
